import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { X, Upload, Loader2, Search, Image as ImageIcon, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ABS = (path) => (path?.startsWith("http") ? path : `${process.env.REACT_APP_BACKEND_URL}${path}`);

/**
 * MediaPicker — modal that lists all Media Library images.
 * Click an image to select it; calls onPick(url) and closes.
 * Also supports inline upload (uses the same /admin/media/upload endpoint).
 */
export default function MediaPicker({ token, onPick, onClose, currentUrl = "" }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const reload = async () => {
    setLoading(true);
    setError("");
    try {
      const r = await axios.get(`${API}/admin/media`, { headers: { "X-Admin-Token": token } });
      setItems(r.data || []);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Failed to load media.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  const handleUpload = async (files) => {
    if (!files?.length) return;
    setError("");
    setUploading(true);
    try {
      let lastUrl = "";
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("alt", file.name);
        const r = await axios.post(`${API}/admin/media/upload`, fd, {
          headers: { "X-Admin-Token": token, "Content-Type": "multipart/form-data" },
        });
        lastUrl = r.data?.url || ABS(r.data?.relative_url || `/api/media/${r.data?.id}`);
      }
      await reload();
      // Auto-select the most-recent upload
      if (lastUrl) {
        onPick?.(lastUrl);
        onClose?.();
      }
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Upload failed.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const filtered = items.filter((m) => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (m.filename || "").toLowerCase().includes(q) || (m.alt || "").toLowerCase().includes(q);
  });

  const pick = (m) => {
    const url = ABS(m.url || `/api/media/${m.id}`);
    onPick?.(url);
    onClose?.();
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
      data-testid="media-picker-modal"
      onClick={onClose}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl my-8" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-cream-200 sticky top-0 bg-white rounded-t-2xl z-10">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-gold font-semibold">Media Library</div>
            <h2 className="font-display text-xl text-navy-deep mt-0.5">Pick an image</h2>
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="media-picker-close" className="w-10 h-10 rounded-full hover:bg-cream-100 flex items-center justify-center">
            <X size={20} className="text-navy" />
          </button>
        </div>

        {/* Toolbar */}
        <div className="px-6 py-3 border-b border-cream-200 flex flex-wrap items-center gap-3 sticky top-[73px] bg-white z-10">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-navy/40" size={16} />
            <input
              data-testid="media-picker-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by filename…"
              className="w-full pl-9 pr-4 py-2.5 rounded-full border border-cream-200 bg-cream-50 focus-gold focus:outline-none text-sm"
            />
          </div>
          <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => handleUpload(e.target.files)} />
          <button
            type="button"
            data-testid="media-picker-upload"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="btn-gold px-5 py-2.5 rounded-full text-sm inline-flex items-center gap-2 disabled:opacity-50"
          >
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {uploading ? "Uploading…" : "Upload new"}
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 min-h-[300px]">
          {error && <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm">{error}</div>}

          {loading ? (
            <div className="flex items-center justify-center py-16 text-navy/55">
              <Loader2 size={20} className="animate-spin mr-2" /> Loading library…
            </div>
          ) : filtered.length === 0 ? (
            <div className="border-2 border-dashed border-cream-200 rounded-2xl p-12 text-center"
              onDrop={(e) => { e.preventDefault(); handleUpload(e.dataTransfer.files); }}
              onDragOver={(e) => e.preventDefault()}>
              <ImageIcon size={36} className="text-navy/30 mx-auto mb-3" />
              <p className="text-navy/60">{items.length === 0 ? "No images yet." : "No matches."}</p>
              <p className="text-xs text-navy/45 mt-1">Drag images here, or click <strong>Upload new</strong>.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3"
              onDrop={(e) => { e.preventDefault(); handleUpload(e.dataTransfer.files); }}
              onDragOver={(e) => e.preventDefault()}>
              {filtered.map((m) => {
                const url = ABS(m.url || `/api/media/${m.id}`);
                const isCurrent = currentUrl && (url === currentUrl || currentUrl.endsWith(`/api/media/${m.id}`));
                return (
                  <button
                    key={m.id}
                    type="button"
                    data-testid={`media-picker-item-${m.id}`}
                    onClick={() => pick(m)}
                    className={`group relative rounded-xl overflow-hidden bg-cream-50 border-2 text-left transition ${
                      isCurrent ? "border-gold ring-2 ring-gold/40" : "border-cream-200 hover:border-gold"
                    }`}
                  >
                    <div className="aspect-square bg-white">
                      <img src={url} alt={m.alt || m.filename} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                    <div className="p-2">
                      <p className="text-[0.7rem] font-medium text-navy truncate" title={m.filename}>{m.filename}</p>
                    </div>
                    {isCurrent && (
                      <span className="absolute top-2 right-2 w-7 h-7 rounded-full bg-gold text-navy-deep flex items-center justify-center shadow-md">
                        <Check size={14} strokeWidth={3} />
                      </span>
                    )}
                    <span className="absolute inset-0 bg-navy-deep/0 group-hover:bg-navy-deep/10 transition pointer-events-none" />
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
